import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerCombobox } from '../crm/CustomerCombobox';

interface CustomerOption {
  id: string;
  name: string;
}

interface CustomerPickerProps {
  options: CustomerOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * "Gilt fuer" -- die Kundenzuordnung eines Artikels (F4-10, F4-25, K-113).
 *
 * **Vorher war das ein ``<select multiple size={6}>``.** Bei achtzig Kunden
 * ist das ein Guckloch: Man kann nicht suchen, die getroffene Auswahl steht
 * ausserhalb des sichtbaren Ausschnitts -- und ein Klick ohne Steuerungstaste
 * loescht sie vollstaendig. Das letzte ist der Grund, aus dem es hier weg
 * musste: Der Fehlgriff ist lautlos, und was er zerstoert, ist die Grenze
 * eines Artikels im Portal.
 *
 * Jetzt: oben, was gewaehlt ist, als abwaehlbare Marken -- darunter ein
 * Auswahlfeld, in das man tippen kann. Ein Klick fuegt genau einen Kunden
 * hinzu, ein Klick auf die Marke nimmt ihn wieder heraus.
 *
 * **Die Suche fragt den Server.** Die Liste mit Kaestchen davor filterte die
 * ersten 200 Kunden im Browser; wer dahinter lag, liess sich nicht zuordnen.
 * ``options`` dient nur noch dazu, die Namen bereits zugeordneter Kunden
 * anzuzeigen.
 *
 * **Keine Auswahl heisst "gilt fuer alle".** Das ist die Vorgabe und der
 * haeufigere Fall; so steht es auch in der Datenbank, wo kein Eintrag in
 * ``kb_article_customers`` dasselbe bedeutet. Ein zusaetzlicher Schalter
 * "alle / bestimmte" waere ein zweiter Zustand fuer denselben Sachverhalt und
 * koennte von der Auswahl abweichen. Deshalb steht der Satz als Hinweis unter
 * der Liste und nicht als Bedienelement darueber.
 */
export function CustomerPicker({ options, value, onChange }: CustomerPickerProps) {
  const { t } = useTranslation();
  // Namen der in dieser Sitzung gewaehlten Kunden -- sie stehen nicht
  // zwingend in ``options``.
  const [chosenNames, setChosenNames] = useState<Record<string, string>>({});

  const nameOf = (id: string) =>
    chosenNames[id] ??
    options.find((option) => option.id === id)?.name ??
    t('knowledge.unknownCustomer');

  return (
    <div className="kb-customers" role="group" aria-label={t('knowledge.customers')}>
      {value.length > 0 ? (
        <div className="kb-customers__chosen">
          {value.map((id) => {
            const name = nameOf(id);
            return (
              <button
                key={id}
                type="button"
                className="kb-chip"
                aria-label={t('knowledge.removeCustomer', { name })}
                onClick={() => {
                  onChange(value.filter((item) => item !== id));
                }}
              >
                {name}
                <span className="kb-chip__remove" aria-hidden="true">
                  ×
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <CustomerCombobox
        ariaLabel={t('knowledge.filterCustomers')}
        value={null}
        excludeIds={value}
        placeholder={t('knowledge.filterCustomers')}
        onChange={(option) => {
          if (option === null) return;
          setChosenNames((current) => ({ ...current, [option.id]: option.label }));
          onChange([...value, option.id]);
        }}
      />

      <span className="field__hint">
        {value.length === 0
          ? t('knowledge.customersAll')
          : t('knowledge.customersSelected', { count: value.length })}
      </span>
    </div>
  );
}
